import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';
import WorkflowQueueCard from '../common/WorkflowQueueCard';
import '../../assets/css/compliance.css';

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0h 0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value)}%`;
}

function formatHoursFromMinutes(minutes) {
  if (!Number.isFinite(minutes)) return '0h';
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

function severityLabel(severity) {
  return severity === 'critical' ? 'Critical' : 'Warning';
}

function severityClass(severity) {
  return severity === 'critical' ? 'comp-badge--critical' : 'comp-badge--warning';
}

function clampInput(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

function emptyPolicy() {
  return {
    maxDailyMinutes: 480,
    maxWeeklyMinutes: 2400,
    maxMonthlyMinutes: 9600,
    breakAfterMinutes: 360,
    breakRequiredMinutes6: 30,
    breakRequiredMinutes9: 45,
    restPeriodMinutes: 660,
    graceMissingCheckoutMinutes: 60,
    requireCheckInOut: true,
    trackingEnforcement: 'MANDATORY',
    breakEscalationMode: 'GRACE',
    breakEscalationGraceMinutes: 720,
    breakRepeatThreshold: 2,
    coreHoursStart: '09:00',
    coreHoursEnd: '17:00',
    coverageMinimumHeadcount: 1,
  };
}

function formatDateInput(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default function ComplianceDashboardPage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const isPrivileged = auth.is_workspace_admin === 1 || ['ADMIN', 'MANAGER'].includes(auth.workspace_role);

  const [dashboard, setDashboard] = useState(null);
  const [policyForm, setPolicyForm] = useState(emptyPolicy());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [overrideForm, setOverrideForm] = useState({
    user_id: '',
    maxDailyHours: '',
    maxWeeklyHours: '',
    reason: '',
    effective_from: '',
    effective_to: '',
  });

  const authHeaders = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${auth.token}`,
      'Content-Type': 'application/json',
    },
  }), [auth.token]);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/compliance/dashboard?workspace_id=${auth.workspace_id}`, authHeaders);
      setDashboard(res.data);
      setPolicyForm({
        ...emptyPolicy(),
        ...(res.data?.policy?.settings || {}),
      });
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || 'Failed to load compliance dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPrivileged) loadDashboard();
  }, [isPrivileged, auth.workspace_id]);

  const savePolicy = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.put('/api/compliance/policy', JSON.stringify({
        workspace_id: auth.workspace_id,
        settings: {
          ...policyForm,
          maxDailyMinutes: parseInt(policyForm.maxDailyMinutes, 10),
          maxWeeklyMinutes: parseInt(policyForm.maxWeeklyMinutes, 10),
          maxMonthlyMinutes: parseInt(policyForm.maxMonthlyMinutes, 10),
          breakAfterMinutes: parseInt(policyForm.breakAfterMinutes, 10),
          breakRequiredMinutes6: parseInt(policyForm.breakRequiredMinutes6, 10),
          breakRequiredMinutes9: parseInt(policyForm.breakRequiredMinutes9, 10),
          restPeriodMinutes: parseInt(policyForm.restPeriodMinutes, 10),
          graceMissingCheckoutMinutes: parseInt(policyForm.graceMissingCheckoutMinutes, 10),
          coverageMinimumHeadcount: parseInt(policyForm.coverageMinimumHeadcount, 10),
          breakEscalationGraceMinutes: parseInt(policyForm.breakEscalationGraceMinutes, 10),
          breakRepeatThreshold: parseInt(policyForm.breakRepeatThreshold, 10),
          requireCheckInOut: Boolean(policyForm.requireCheckInOut),
          trackingEnforcement: policyForm.trackingEnforcement,
          breakEscalationMode: policyForm.breakEscalationMode,
          coreHoursStart: policyForm.coreHoursStart,
          coreHoursEnd: policyForm.coreHoursEnd,
        },
      }), authHeaders);
      setDashboard((prev) => prev ? { ...prev, policy: res.data.policy } : prev);
      setSuccess('Compliance policy saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || 'Failed to save compliance policy.');
    } finally {
      setSaving(false);
    }
  };

  const saveOverride = async () => {
    if (!overrideForm.user_id) {
      setError('Please select an employee.');
      return;
    }

    setOverrideSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.post('/api/compliance/policy-overrides', JSON.stringify({
        workspace_id: auth.workspace_id,
        user_id: parseInt(overrideForm.user_id, 10),
        settings: {
          ...(overrideForm.maxDailyHours ? { maxDailyMinutes: Math.round(parseFloat(overrideForm.maxDailyHours) * 60) } : {}),
          ...(overrideForm.maxWeeklyHours ? { maxWeeklyMinutes: Math.round(parseFloat(overrideForm.maxWeeklyHours) * 60) } : {}),
        },
        reason: overrideForm.reason,
        effective_from: overrideForm.effective_from || null,
        effective_to: overrideForm.effective_to || null,
      }), authHeaders);
      setSuccess('Employee override saved.');
      setOverrideForm({
        user_id: '',
        maxDailyHours: '',
        maxWeeklyHours: '',
        reason: '',
        effective_from: '',
        effective_to: '',
      });
      await loadDashboard();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || 'Failed to save override.');
    } finally {
      setOverrideSaving(false);
    }
  };

  const removeOverride = async (id) => {
    setError('');
    try {
      await axios.delete(`/api/compliance/policy-overrides/${id}`, authHeaders);
      setSuccess('Override removed.');
      await loadDashboard();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || 'Failed to remove override.');
    }
  };

  const summary = dashboard?.summary || {};
  const policy = dashboard?.policy?.settings || emptyPolicy();
  const breakViolationCount = summary.break_violation_count || 0;
  const desktopPolicy = dashboard?.workspace?.desktop_policy || {
    tracking_enabled: false,
    enforcement: 'OPTIONAL',
    privacy_mode: 'METADATA_ONLY',
  };
  const desktopTrackingRequired = Boolean(desktopPolicy.tracking_enabled) && desktopPolicy.enforcement === 'REQUIRED';
  const coverage = dashboard?.coverage || { slots: [], gaps: [], activeNow: [] };
  const tracked = dashboard?.tracked_vs_untracked || { users: [] };
  const members = dashboard?.members || [];
  const overrides = dashboard?.policy_overrides || [];

  if (!isPrivileged) {
    return (
      <div>
        <Sidebar />
        <div className="comp-page">
          <div className="comp-empty">
            <h2>{tr("Compliance Dashboard")}</h2>
            <p>{tr("You do not have permission to view compliance data.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Sidebar />
      <div className="comp-page">
        <div className="comp-header">
          <div>
            <div className="comp-kicker">{tr("Monday compliance brief")}</div>
            <h1>{tr("Compliance Dashboard")}</h1>
            <p>{tr("Review overtime, breaks, missing check-outs, and coverage in one weekly workflow.")}</p>
          </div>
          <div className="comp-header-actions">
            <button className="comp-btn comp-btn--secondary" onClick={loadDashboard} disabled={loading}>{tr("Refresh")}</button>
            <Link className="comp-btn comp-btn--primary" to="/attendance">{tr("Open Attendance")}</Link>
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <WorkflowQueueCard
            workspaceId={auth.workspace_id}
            token={auth.token}
            title={tr("Open Workflow Queue")}
            actionLabel="Open Leave"
            actionUrl="/leave"
          />
        </div>

        {error && <div className="comp-alert comp-alert--error">{error}</div>}
        {success && <div className="comp-alert comp-alert--success">{success}</div>}

        {loading || !dashboard ? (
          <div className="comp-loading">{tr("Loading compliance data...")}</div>
        ) : (
          <>
            <div className="comp-brief">
              {dashboard.monday_brief?.map((item) => (
                <span key={item} className="comp-brief-pill">{item}</span>
              ))}
            </div>

            <div className="comp-stats">
              <div className="comp-stat-card">
                <div className="comp-stat-label">{tr("Critical issues")}</div>
                <div className="comp-stat-value comp-stat-value--critical">{summary.critical_count || 0}</div>
                <div className="comp-stat-hint">{tr("Needs action today")}</div>
              </div>
              <div className="comp-stat-card">
                <div className="comp-stat-label">{tr("Warnings")}</div>
                <div className="comp-stat-value">{summary.warning_count || 0}</div>
                <div className="comp-stat-hint">{tr("Review this week")}</div>
              </div>
              <div className="comp-stat-card">
                <div className="comp-stat-label">{tr("Missing check-outs")}</div>
                <div className="comp-stat-value">{summary.missing_checkout_count || 0}</div>
                <div className="comp-stat-hint">{tr("Payroll blockers")}</div>
              </div>
              <div className="comp-stat-card">
                <div className="comp-stat-label">{tr("Break compliance")}</div>
                <div className={`comp-stat-value${breakViolationCount > 0 ? ' comp-stat-value--critical' : ' comp-stat-value--healthy'}`}>
                  {breakViolationCount > 0 ? breakViolationCount : tr("Active")}
                </div>
                <div className="comp-stat-hint">
                  {breakViolationCount > 0
                    ? `${breakViolationCount} break violation${breakViolationCount === 1 ? '' : 's'} flagged`
                    : `${policy.breakRequiredMinutes6 || 30} min after 6h · ${policy.breakRequiredMinutes9 || 45} min after 9h`}
                </div>
              </div>
              <div className="comp-stat-card">
                <div className="comp-stat-label">{tr("Tracked vs untracked")}</div>
                <div className="comp-stat-value">{formatPercent(summary.tracked_pct || 0)}</div>
                <div className="comp-stat-hint">{tr("Desktop tracking coverage")}</div>
              </div>
            </div>

            <div className="comp-grid">
              <section className="comp-panel comp-panel--wide">
                <div className="comp-panel-head">
                  <div>
                    <h2>{tr("Action Queue")}</h2>
                    <p>{tr("Fix the highest-risk items first. Each row explains why it was flagged.")}</p>
                  </div>
                  <Link to="/attendance" className="comp-link">{tr("Open attendance")}</Link>
                </div>

                <div className="comp-issue-list">
                  {(dashboard.issues?.action_queue || []).length === 0 ? (
                    <div className="comp-empty-inline">{tr("No open compliance issues right now.")}</div>
                  ) : dashboard.issues.action_queue.map((issue, index) => {
                    const name = [issue.user?.first_name, issue.user?.last_name].filter(Boolean).join(' ') || issue.user?.email || tr("Unknown");
                    return (
                      <div className="comp-issue" key={`${issue.type}-${issue.user?.id || index}-${issue.date || index}`}>
                        <div className="comp-issue-main">
                          <div className={`comp-badge ${severityClass(issue.severity)}`}>{severityLabel(issue.severity)}</div>
                          <div>
                            <div className="comp-issue-title">{issue.title}</div>
                            <div className="comp-issue-meta">{name}</div>
                          </div>
                        </div>
                        <div className="comp-issue-body">
                          <div className="comp-issue-reason">{issue.reason}</div>
                          <div className="comp-issue-explainer">{issue.explanation}</div>
                        </div>
                        <div className="comp-issue-actions">
                          <Link to={issue.action_url || '/attendance'} className="comp-btn comp-btn--secondary">{tr("Review")}</Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <aside className="comp-panel">
                <div className="comp-panel-head">
                  <div>
                    <h2>{tr("Policy Controls")}</h2>
                    <p>{tr("Keep the rules simple and workspace-wide.")}</p>
                  </div>
                </div>

                <div className="comp-form">
                  <div className="comp-rule-summary">
                    <div className="comp-rule-summary-head">{tr("Monitored rules")}</div>
                    <div className="comp-rule-chip-row">
                      <span className="comp-rule-chip">{tr("Breaks:")} {policyForm.breakRequiredMinutes6 || 30} {tr("min after")} {formatHoursFromMinutes(policyForm.breakAfterMinutes || 360)}</span>
                      <span className="comp-rule-chip">{tr("Extended shifts:")} {policyForm.breakRequiredMinutes9 || 45} {tr("min after 9h")}</span>
                      <span className="comp-rule-chip">{tr("Rest period target:")} {formatHoursFromMinutes(policyForm.restPeriodMinutes || 660)}</span>
                    </div>
                  </div>
                  <div className="comp-form-row">
                    <label>{tr("Max daily hours")}</label>
                    <input className="comp-input" type="number" min="1" step="0.5" value={clampInput(policyForm.maxDailyMinutes / 60)} onChange={(e) => setPolicyForm((prev) => ({ ...prev, maxDailyMinutes: Number(e.target.value) * 60 }))} />
                  </div>
                  <div className="comp-form-row">
                    <label>{tr("Max weekly hours")}</label>
                    <input className="comp-input" type="number" min="1" step="0.5" value={clampInput(policyForm.maxWeeklyMinutes / 60)} onChange={(e) => setPolicyForm((prev) => ({ ...prev, maxWeeklyMinutes: Number(e.target.value) * 60 }))} />
                  </div>
                  <div className="comp-form-row">
                    <label>{tr("Max monthly hours")}</label>
                    <input className="comp-input" type="number" min="1" step="0.5" value={clampInput(policyForm.maxMonthlyMinutes / 60)} onChange={(e) => setPolicyForm((prev) => ({ ...prev, maxMonthlyMinutes: Number(e.target.value) * 60 }))} />
                  </div>
                  <div className="comp-form-row comp-form-row--split">
                    <div>
                      <label>{tr("Break after")}</label>
                      <input className="comp-input" type="number" min="30" step="15" value={policyForm.breakAfterMinutes} onChange={(e) => setPolicyForm((prev) => ({ ...prev, breakAfterMinutes: Number(e.target.value) }))} />
                    </div>
                    <div>
                      <label>{tr("6h break")}</label>
                      <input className="comp-input" type="number" min="0" step="15" value={policyForm.breakRequiredMinutes6} onChange={(e) => setPolicyForm((prev) => ({ ...prev, breakRequiredMinutes6: Number(e.target.value) }))} />
                    </div>
                  </div>
                  <div className="comp-form-row comp-form-row--split">
                    <div>
                      <label>{tr("9h break")}</label>
                      <input className="comp-input" type="number" min="0" step="15" value={policyForm.breakRequiredMinutes9} onChange={(e) => setPolicyForm((prev) => ({ ...prev, breakRequiredMinutes9: Number(e.target.value) }))} />
                    </div>
                    <div>
                      <label>{tr("Rest period")}</label>
                      <input className="comp-input" type="number" min="600" step="15" value={policyForm.restPeriodMinutes} onChange={(e) => setPolicyForm((prev) => ({ ...prev, restPeriodMinutes: Number(e.target.value) }))} />
                    </div>
                  </div>
                  <div className="comp-form-row comp-form-row--split">
                    <div>
                      <label>{tr("Core hours start")}</label>
                      <input className="comp-input" type="time" value={policyForm.coreHoursStart} onChange={(e) => setPolicyForm((prev) => ({ ...prev, coreHoursStart: e.target.value }))} />
                    </div>
                    <div>
                      <label>{tr("Core hours end")}</label>
                      <input className="comp-input" type="time" value={policyForm.coreHoursEnd} onChange={(e) => setPolicyForm((prev) => ({ ...prev, coreHoursEnd: e.target.value }))} />
                    </div>
                  </div>
                  <div className="comp-form-row comp-form-row--split">
                    <div>
                      <label>{tr("Checkout grace")}</label>
                      <input className="comp-input" type="number" min="5" step="5" value={policyForm.graceMissingCheckoutMinutes} onChange={(e) => setPolicyForm((prev) => ({ ...prev, graceMissingCheckoutMinutes: Number(e.target.value) }))} />
                    </div>
                    <div>
                      <label>{tr("Tracking enforcement")}</label>
                      <select className="comp-input" value={policyForm.trackingEnforcement} onChange={(e) => setPolicyForm((prev) => ({ ...prev, trackingEnforcement: e.target.value }))}>
                        <option value="MANDATORY">{tr("Mandatory")}</option>
                        <option value="OPTIONAL">{tr("Optional")}</option>
                      </select>
                    </div>
                  </div>
                  <div className="comp-form-row comp-form-row--split">
                    <div>
                      <label>{tr("Break escalation")}</label>
                      <select className="comp-input" value={policyForm.breakEscalationMode} onChange={(e) => setPolicyForm((prev) => ({ ...prev, breakEscalationMode: e.target.value }))}>
                        <option value="GRACE">{tr("After grace period")}</option>
                        <option value="STRICT">{tr("Escalate immediately")}</option>
                      </select>
                    </div>
                    <div>
                      <label>{tr("Repeat threshold")}</label>
                      <input className="comp-input" type="number" min="1" step="1" value={policyForm.breakRepeatThreshold} onChange={(e) => setPolicyForm((prev) => ({ ...prev, breakRepeatThreshold: Number(e.target.value) }))} />
                    </div>
                  </div>
                  <div className="comp-form-row">
                    <label>{tr("Break escalation grace (minutes)")}</label>
                    <input className="comp-input" type="number" min="0" step="30" value={policyForm.breakEscalationGraceMinutes} onChange={(e) => setPolicyForm((prev) => ({ ...prev, breakEscalationGraceMinutes: Number(e.target.value) }))} />
                  </div>
                  <div className="comp-form-row">
                    <label>{tr("Require check-in/out")}</label>
                    <label className="comp-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(policyForm.requireCheckInOut)}
                        onChange={(e) => setPolicyForm((prev) => ({ ...prev, requireCheckInOut: e.target.checked }))}
                      />
                      <span>{tr("Yes, enforce check-in and check-out")}</span>
                    </label>
                  </div>
                  <div className="comp-form-row">
                    <label>{tr("Minimum headcount for coverage")}</label>
                    <input className="comp-input" type="number" min="1" step="1" value={policyForm.coverageMinimumHeadcount} onChange={(e) => setPolicyForm((prev) => ({ ...prev, coverageMinimumHeadcount: Number(e.target.value) }))} />
                  </div>
                  <button className="comp-btn comp-btn--primary comp-btn--full" onClick={savePolicy} disabled={saving}>
                    {saving ? tr("Saving...") : tr("Save policy")}
                  </button>
                </div>

                <div className="comp-policy-note">
                  {policy.requireCheckInOut ? tr("Arbeitszeit should be fully recorded so overtime and break rules are visible.") : tr("Check-in/out enforcement is currently relaxed.")}
                </div>
              </aside>
            </div>

            <div className="comp-grid comp-grid--bottom">
              <section className="comp-panel">
                <div className="comp-panel-head">
                  <div>
                    <h2>{tr("Employee Overrides")}</h2>
                    <p>{tr("Use this for temporary exceptions or agreed arrangements.")}</p>
                  </div>
                </div>

                <div className="comp-form">
                  <div className="comp-form-row">
                    <label>{tr("Employee")}</label>
                    <select
                      className="comp-input"
                      value={overrideForm.user_id}
                      onChange={(e) => setOverrideForm((prev) => ({ ...prev, user_id: e.target.value }))}
                    >
                      <option value="">{tr("Select employee")}</option>
                      {members.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.name} ({member.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="comp-form-row comp-form-row--split">
                    <div>
                      <label>{tr("Daily hours")}</label>
                      <input
                        className="comp-input"
                        type="number"
                        min="1"
                        step="0.5"
                        value={overrideForm.maxDailyHours}
                        onChange={(e) => setOverrideForm((prev) => ({ ...prev, maxDailyHours: e.target.value }))}
                        placeholder={String((policy.maxDailyMinutes || 480) / 60)}
                      />
                    </div>
                    <div>
                      <label>{tr("Weekly hours")}</label>
                      <input
                        className="comp-input"
                        type="number"
                        min="1"
                        step="0.5"
                        value={overrideForm.maxWeeklyHours}
                        onChange={(e) => setOverrideForm((prev) => ({ ...prev, maxWeeklyHours: e.target.value }))}
                        placeholder={String((policy.maxWeeklyMinutes || 2400) / 60)}
                      />
                    </div>
                  </div>
                  <div className="comp-form-row">
                    <label>{tr("Reason")}</label>
                    <input
                      className="comp-input"
                      value={overrideForm.reason}
                      onChange={(e) => setOverrideForm((prev) => ({ ...prev, reason: e.target.value }))}
                      placeholder={tr("e.g. Project sprint until Friday")}
                    />
                  </div>
                  <div className="comp-form-row comp-form-row--split">
                    <div>
                      <label>{tr("Valid from")}</label>
                      <input
                        className="comp-input"
                        type="date"
                        value={overrideForm.effective_from}
                        onChange={(e) => setOverrideForm((prev) => ({ ...prev, effective_from: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label>{tr("Valid to")}</label>
                      <input
                        className="comp-input"
                        type="date"
                        value={overrideForm.effective_to}
                        onChange={(e) => setOverrideForm((prev) => ({ ...prev, effective_to: e.target.value }))}
                      />
                    </div>
                  </div>
                  <button className="comp-btn comp-btn--primary comp-btn--full" onClick={saveOverride} disabled={overrideSaving}>
                    {overrideSaving ? tr("Saving...") : tr("Save employee override")}
                  </button>
                </div>
              </section>

              <section className="comp-panel">
                <div className="comp-panel-head">
                  <div>
                    <h2>{tr("Active Overrides")}</h2>
                    <p>{tr("Review current exceptions and remove them when they expire.")}</p>
                  </div>
                </div>

                <div className="comp-override-list">
                  {(overrides || []).length === 0 ? (
                    <div className="comp-empty-inline">{tr("No active overrides.")}</div>
                  ) : overrides.map((override) => (
                    <div className="comp-override-item" key={override.id}>
                      <div>
                        <strong>{override.user ? [override.user.first_name, override.user.last_name].filter(Boolean).join(' ') || override.user.email : `User ${override.user_id}`}</strong>
                        <span>
                          {override.settings?.maxDailyMinutes ? `${override.settings.maxDailyMinutes / 60}h daily` : tr("Daily default")}
                          {' · '}
                          {override.settings?.maxWeeklyMinutes ? `${override.settings.maxWeeklyMinutes / 60}h weekly` : tr("Weekly default")}
                        </span>
                        <small>{override.reason || tr("No reason recorded")}</small>
                        {(override.effective_from || override.effective_to) && (
                          <small>
                            {formatDateInput(override.effective_from) || tr("Now")}
                            {' → '}
                            {formatDateInput(override.effective_to) || tr("open ended")}
                          </small>
                        )}
                      </div>
                      <button className="comp-btn comp-btn--secondary" onClick={() => removeOverride(override.id)}>{tr("Remove")}</button>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="comp-grid comp-grid--bottom">
              <section className="comp-panel">
                <div className="comp-panel-head">
                  <div>
                    <h2>{tr("Coverage Today")}</h2>
                    <p>{tr("Shows who is currently checked in and how many people covered each core-work hour today.")}</p>
                  </div>
                </div>

                <div className="comp-active-row">
                  {(coverage.activeNow || []).length === 0 ? (
                    <div className="comp-empty-inline">{tr("No one is currently checked in.")}</div>
                  ) : coverage.activeNow.map((user) => {
                    const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
                    return <span className="comp-user-chip" key={user.id}>{name}</span>;
                  })}
                </div>

                <div className="comp-hours">
                  {(coverage.slots || []).map((slot) => (
                    <div key={slot.label} className={`comp-hour ${slot.is_gap ? 'comp-hour--gap' : ''}`}>
                      <span>{slot.label}</span>
                      <strong>{slot.count}</strong>
                    </div>
                  ))}
                </div>

                <div className="comp-note">{tr("Each hour shows the number of checked-in team members present during that time. The green chip shows who is currently checked in. Red hours mean coverage was below the required minimum headcount and may need follow-up.")} </div>
              </section>

              <section className="comp-panel">
                <div className="comp-panel-head">
                  <div>
                    <h2>{tr("Tracked vs Untracked")}</h2>
                    <p>
                      {desktopTrackingRequired
                        ? tr("Desktop coverage compared with attendance time for the last week.")
                        : tr("Desktop tracking is optional for this workspace, so tracked vs untracked data may be incomplete.")}
                    </p>
                  </div>
                </div>

                {desktopTrackingRequired ? (
                  <>
                    <div className="comp-progress">
                      <div className="comp-progress-bar">
                        <div className="comp-progress-fill" style={{ width: `${summary.tracked_pct || 0}%` }} />
                      </div>
                      <div className="comp-progress-meta">
                        <span>{tr("Tracked")} {formatDuration(tracked.tracked_seconds || 0)}</span>
                        <span>{tr("Untracked")} {formatDuration(tracked.untracked_seconds || 0)}</span>
                      </div>
                    </div>

                    <div className="comp-table">
                      {(tracked.users || []).slice(0, 5).map((row) => {
                        const name = [row.user?.first_name, row.user?.last_name].filter(Boolean).join(' ') || row.user?.email;
                        return (
                          <div className="comp-table-row" key={row.user_id}>
                            <div>
                              <strong>{name}</strong>
                              <span>{row.tracked_pct}{tr("% tracked")}</span>
                            </div>
                            <div>{formatDuration(row.untracked_seconds)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="comp-note">{tr("Desktop tracking is currently optional or turned off. This comparison is only meaningful when the workspace requires desktop tracking and team devices are actively sending heartbeat data.")} </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
